import type { EthereumAddress, } from "@bracket/types";
import { awaitTruthy, } from "@store/helpers";
import type { IBracketStore, } from "@store/types";
import { amountStringToBi, biToAmountString, } from "@utils";
import { produce, } from 'immer';
import type { StateCreator, } from "zustand";
import type { Store, } from '../useStore';
import * as N from "./notifications";
import * as R from "./resolvers";
import type{ IWrapSlice, } from "./types";



export const createWrapSlice: StateCreator<IBracketStore, [], [], IWrapSlice> = ( set, get, ) => {



	const fetch = async () => {

		const { fetching, ready, } = get().wrap;
		if(fetching || ready) {
			return;
		}

		set(produce<Store>(state => {
			state.wrap.fetching = true;
		}));

		const { useStore, } = await import("@store");
		await awaitTruthy(useStore, state => state.wallet.ready);
		await awaitTruthy(useStore, state => state.vaults.ready);

		_setDefaults();
		_applyActiveVault();
		_syncData();

		set(produce<Store>(state => {
			state.wrap.ready = true;
			state.wrap.fetching = false;
		}));

	}


	const reset = () => {
		resetValues();
		_setDefaults();
		set(produce<Store>(state => {
			state.wrap.ready = false;
			state.wrap.fetching = false;
		}));
	};


	const resetAsync = async () => {
		reset();
	};


	const setMode = ( mode: "WRAP" | "UNWRAP" ) => {
		set(produce<Store>(state => {
			state.wrap.mode = mode;
		}));
		_resetAmounts();
		_applyCapable();
	};


	const resetValues = () => {
		set(produce<Store>(state => {
			state.wrap.vault_amount = "";
			state.wrap.wrapper_amount = "";
		}));
	};


	const wrap = async () => {

		set(produce<Store>(state => {
			state.wrap.busy = true;
		}));

		try {

			const { vault_address, wrapper, vault_amount, decimals, vault_label, } = get().wrap;
			if(!wrapper) throw new Error("wrapper is null");
			if(!vault_address) throw new Error("vault_address is null");

			const { address, } = wrapper;
			const { success, error, hash, canceled, } = await R.wrap({
				vault: vault_address,
				wrapper: address,
				amount: amountStringToBi(vault_amount, decimals),
			});

			if(success) {
				get().notificationSlice.addNotification(N.wrappedSuccessNotification(hash, vault_label, vault_amount));
			}
			else if(canceled) {
				// do nothing, user is cancelling the transaction and doesn't need notification.
			}
			else {
				throw error;
			}


			set(produce<Store>(state => {
				state.wrap.busy = false;
			}));

			await _syncData();
			_resetAmounts();

		}
		catch(err) {
			console.error(err);
		}
	};


	const unwrap = async () => {

		set(produce<Store>(state => {
			state.wrap.busy = true;
		}));

		try {

			const { vault_address, wrapper, wrapper_amount, decimals, } = get().wrap;
			if(!wrapper) {
				throw new Error("wrapper is null");
			}

			const { address, } = wrapper;

			const { success, error, hash, canceled, } = await R.unwrap({
				vault: vault_address,
				wrapper: address,
				amount: amountStringToBi(wrapper_amount, decimals),
			});

			if(success) {
				get().notificationSlice.addNotification(N.unwrappedSuccessNotification(hash, wrapper.name, wrapper_amount));
			}
			else if(canceled) {

			}
			else {
				throw error;
			}

			set(produce<Store>(state => {
				state.wrap.busy = false;
			}));

			await _syncData();
			_resetAmounts();

		}
		catch(err) {
			console.error(err);
		}
	};


	const	setVaultAmount = ( amount: string ) => {
		if(amount.length > 0 && (!/^[0-9.]+$/.test(amount) || amount.startsWith(".") || amount.includes("..")) ) {
			return;
		}
		if(amount.includes('.') && (amount.match(/\./g) || []).length > 1) {
			return;
		}
		set(produce<Store>(state => {
			state.wrap.vault_amount = amount;
		}));
		_applyCapable();
	};


	const setWrapperAmount = ( amount: string ) => {
		if(amount.length > 0 && (!/^[0-9.]+$/.test(amount) || amount.startsWith(".") || amount.includes("..")) ) {
			return;
		}
		set(produce<Store>(state => {
			state.wrap.wrapper_amount = amount;
		}));
		_applyCapable();
	};


	const setMax = () => {
		const { mode, } = get().wrap;
		if(mode === "WRAP") {
			set(produce<Store>(state => {
				const { vault_balance, decimals, } = state.wrap;
				if(vault_balance === null) {
					throw new Error("vault_balance is null");
				}
				state.wrap.vault_amount = biToAmountString(vault_balance, decimals);
			}));
		} else {
			set(produce<Store>(state => {
				const { wrapper_balance, decimals, } = state.wrap;
				if(wrapper_balance === null) {
					throw new Error("wrapper_balance is null");
				}
				state.wrap.wrapper_amount = biToAmountString(wrapper_balance, decimals);
			}));
		}
		_applyCapable();
	};


	const _setDefaults = () => {
		set(produce<Store>(state => {
			state.wrap.wrappable = false;
			state.wrap.wrapper = null;
			state.wrap.wrapper_balance = null;
			state.wrap.vault_address = null;
			state.wrap.vault_balance = null;
		}));
	};


	const _applyActiveVault = () => {
		const { active, } = get().vaults;
		if(active && Boolean(active.wrapper)) {
			set(produce<Store>(state => {
				state.wrap.wrappable = true;
				state.wrap.vault_address = active.address;
				state.wrap.wrapper = active.wrapper;
				state.wrap.wrapper_balance = 0n;
				state.wrap.vault_balance = 0n;
				state.wrap.decimals = active.denomination.decimals;
				state.wrap.vault_label = active.symbol;
				state.wrap.wrapper_label = active.wrapper.name;
			}));
			return;
		}
		_setDefaults();
	}


	const _syncData = async () => {

		if(get().wrap.wrappable) {

			const { wrapper: { address: wrapper_address, }, vault_address, } = get().wrap;
			const { address, } = get().wallet as { address: EthereumAddress; };

			const { wrapper_balance, vault_balance, } = await R.getBalances({
				wallet: address,
				wrapper: wrapper_address,
				vault: vault_address,
			});

			set(produce<Store>(state => {
				state.wrap.wrapper_balance = wrapper_balance;
				state.wrap.vault_balance = vault_balance;
			}));
		}
	}


	const _resetAmounts = () => {
		set(produce<Store>(state => {
			state.wrap.vault_amount = "0";
			state.wrap.wrapper_amount = "0";
		}));
	}


	const _applyCapable = () => {
		const { wrapper_amount, vault_amount, mode, vault_balance, wrapper_balance, decimals, } = get().wrap;
		if(typeof vault_balance !== "bigint" || typeof wrapper_balance !== "bigint") {
			throw new Error("vault_balance or wrapper_balance is null");
		}
		set(produce<Store>(state => {
			state.wrap.capable = mode === "WRAP" && Number(vault_amount) > 0 || mode === "UNWRAP" && Number(wrapper_amount) > 0;
			state.wrap.excessive = mode === "WRAP" && BigInt(amountStringToBi(vault_amount, decimals)) > vault_balance || mode === "UNWRAP" && BigInt(amountStringToBi(wrapper_amount, decimals)) > wrapper_balance;
		}));
	}


	// place at the bottom of the call stack.
	setTimeout(_ => fetch(), 0);


	return {
		ready: false,
		fetching: false,
		busy: false,
		capable: false,
		decimals: 0,
		excessive: false,
		mode: "WRAP",
		vault_address: null,
		vault_amount: "0",
		vault_balance: null,
		vault_label: null,
		wrappable: false,
		wrapper: null,
		wrapper_amount: "0",
		wrapper_balance: null,
		wrapper_label: null,
		fetch,
		reset,
		resetAsync,
		resetValues,
		setMode,
		setVaultAmount,
		setWrapperAmount,
		setMax,
		wrap,
		unwrap,
	};

};


